if (!customElements.get('calendar-section')) {
    class CalendarSection extends HTMLElement {
        connectedCallback() {
            this.firstDay = parseInt(this.dataset.firstDay ?? '1', 10);
            this.today = new Date();
            this.cursor = new Date(this.today);
            this.view = 'month';
            this.mobileView = window.matchMedia('(max-width: 900px)').matches ? 'cards' : 'calendar';
            this.activeFilters = new Set();
            this.events = this._parseEvents();
            this.text = {
                allCategories: this.dataset.textAllCategories || 'Alla kategorier',
                noEvents: this.dataset.textNoEvents || 'Inga event denna m\u00e5nad.',
                timePrefix: this.dataset.textTimePrefix || 'kl. ',
                buyTickets: this.dataset.textBuyTickets || 'K\u00f6p biljetter',
                soldout: this.dataset.textSoldout || 'Sluts\u00e5ld',
                expired: this.dataset.textExpired || 'Utg\u00e5tt',
                allDates: this.dataset.textAllDates || 'Se alla datum \u2192',
                share: this.dataset.textShare || 'Dela',
                readMore: this.dataset.textReadMore || 'L\u00e4s mer',
                inventory: this.dataset.textInventory || '[inventory] platser kvar',
            };

            this.titleEl = this.querySelector('.cal__title');
            this.weekdaysEl = this.querySelector('.cal__weekdays');
            this.gridEl = this.querySelector('.cal__grid');
            this.calEl = this.querySelector('.cal');
            this.toastEl = this.querySelector('.cal__toast');
            this.filtersEl = this.querySelector('.cal__filters');
            this.legendEl = this.querySelector('.cal__legend');
            this.detailEl = this.querySelector('.cal__detail');
            this.drawerEl = this.querySelector('.cal__drawer');
            this.drawerContentEl = this.querySelector('.cal__drawer-content');
            this.cardListEl = this.querySelector('.cal__card-list');

            this.querySelector('.cal__prev').addEventListener('click', () => this._navigate(-1));
            this.querySelector('.cal__next').addEventListener('click', () => this._navigate(1));
            this.querySelector('.cal__today-btn').addEventListener('click', () => this._goToToday());
            this.querySelectorAll('.cal__view-btn').forEach((btn) =>
                btn.addEventListener('click', () => this._setView(btn.dataset.view))
            );
            this.querySelectorAll('.cal__mobile-view-btn').forEach((btn) =>
                btn.addEventListener('click', () => this._setMobileView(btn.dataset.mobileView))
            );

            // Sync initial mobile-view active state
            this.querySelectorAll('.cal__mobile-view-btn').forEach((btn) => {
                btn.classList.toggle('cal__mobile-view-btn--active', btn.dataset.mobileView === this.mobileView);
            });

            // Event delegation: any click on an event pill or card anywhere in the component
            this.addEventListener('click', (e) => {
                const shareBtn = e.target.closest('.cal__share-btn[data-variant-id]');
                if (shareBtn) {
                    e.stopPropagation();
                    const variantId = shareBtn.dataset.variantId;
                    const ev = this.events.find((ev) => String(ev.variantId) === String(variantId));
                    if (ev) this._shareEvent(ev);
                    return;
                }
                const pill = e.target.closest('.cal__event[data-variant-id], .cal__event-card[data-variant-id]');
                if (!pill) return;
                e.stopPropagation();
                const variantId = pill.dataset.variantId;
                const ev = this.events.find((ev) => String(ev.variantId) === String(variantId));
                if (ev) this._showDetail(ev);
            });

            // Drawer close bindings
            if (this.drawerEl) {
                this.querySelector('.cal__drawer-overlay').addEventListener('click', () => this._closeDrawer());
                this.querySelector('.cal__drawer-close').addEventListener('click', () => this._closeDrawer());
            }
            this._escHandler = (e) => { if (e.key === 'Escape') this._closeDrawer(); };
            document.addEventListener('keydown', this._escHandler);

            this._buildFilters();
            this._buildLegend();
            this._buildWeekdays();
            this._render();
        }

        disconnectedCallback() {
            document.removeEventListener('keydown', this._escHandler);
        }

        // â”€â”€ Events â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

        _parseEvents() {
            const sectionId = this.dataset.sectionId;
            const el = this.querySelector(`#cal-events-${sectionId}`);
            if (!el) {
                console.warn('[Calendar] No events data tag found for section id:', sectionId);
                return [];
            }
            let events = [];
            try {
                events = JSON.parse(el.textContent);
            } catch (e) {
                console.error('[Calendar] Failed to parse events JSON:', e);
                return [];
            }
            console.group(`[Calendar] ${events.length} event variant(s) loaded`);
            if (events.length === 0) {
                console.warn(
                    'No events found. Check:\n' +
                    '  1. A collection is selected in the theme editor\n' +
                    '  2. Products in that collection have variants with the event_date metafield set\n' +
                    '  3. Metafield namespace/key in theme settings matches what is set on the variants'
                );
            } else {
                const byProduct = {};
                events.forEach((ev) => {
                    if (!byProduct[ev.productId]) byProduct[ev.productId] = { title: ev.title, variants: [] };
                    byProduct[ev.productId].variants.push({
                        variantId: ev.variantId,
                        variantTitle: ev.variantTitle,
                        date: ev.date,
                        available: ev.available,
                    });
                });
                Object.values(byProduct).forEach((p) => {
                    console.group(`Product: "${p.title}"`);
                    p.variants.forEach((v) =>
                        console.log(`Variant: "${v.variantTitle}" | date: ${v.date} | available: ${v.available} | id: ${v.variantId}`)
                    );
                    console.groupEnd();
                });
            }
            console.groupEnd();
            events = this._expandMultipleDates(events);
            events = this._expandBlogRecurring(events);
            return events;
        }

        _parseEventDate(str) {
            if (!str) return null;
            if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
                const [y, m, d] = str.split('-').map(Number);
                return { dateKey: str, hasTime: false, date: new Date(y, m - 1, d) };
            }
            const dt = new Date(str);
            if (isNaN(dt)) return null;
            const dateKey = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
            const lang = document.documentElement.lang || 'sv';
            const timeStr = dt.toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' });
            return { dateKey, hasTime: true, timeStr, date: dt };
        }

        _getEventsForDate(dateKey) {
            return this.events
                .map((ev) => ({ ...ev, _parsed: this._parseEventDate(ev.date) }))
                .filter((ev) => {
                    if (!ev._parsed || ev._parsed.dateKey !== dateKey) return false;
                    const excl = Array.isArray(ev.excludedDates) ? ev.excludedDates : [];
                    if (excl.some((d) => this._parseEventDate(d)?.dateKey === dateKey)) return false;
                    if (this.activeFilters.size === 0) return true;
                    const cats = Array.isArray(ev.categories) ? ev.categories : [];
                    return cats.some((c) => this.activeFilters.has(c));
                })
                .sort((a, b) => {
                    if (!a._parsed.hasTime) return -1;
                    if (!b._parsed.hasTime) return 1;
                    return a._parsed.date - b._parsed.date;
                });
        }

        _esc(str) {
            return String(str ?? '')
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;');
        }

        _toDateKey(date) {
            return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        }

        // ── Blog recurring expansion ──────────────────────────────────────

        _addInterval(date, interval) {
            const d = new Date(date);
            switch ((interval || '').toLowerCase()) {
                case 'daily': d.setDate(d.getDate() + 1); break;
                case 'weekly': d.setDate(d.getDate() + 7); break;
                case 'monthly': d.setMonth(d.getMonth() + 1); break;
                case 'yearly': d.setFullYear(d.getFullYear() + 1); break;
                default: return null;
            }
            return d;
        }

        _expandMultipleDates(events) {
            const expanded = [];
            events.forEach((ev) => {
                if (!Array.isArray(ev.date)) {
                    expanded.push({ ...ev, _baseVariantId: ev.variantId });
                    return;
                }
                ev.date.forEach((d, i) => {
                    expanded.push({
                        ...ev,
                        date: d,
                        variantId: i === 0 ? ev.variantId : `${ev.variantId}-d${i}`,
                        _baseVariantId: ev.variantId,
                    });
                });
            });
            return expanded;
        }

        _expandBlogRecurring(events) {
            const cap = new Date();
            cap.setFullYear(cap.getFullYear() + 2);

            const excludedSet = (ev) => {
                const dates = Array.isArray(ev.excludedDates) ? ev.excludedDates : [];
                return new Set(dates.map((d) => this._parseEventDate(d)?.dateKey).filter(Boolean));
            };

            const extra = [];
            events.forEach((ev) => {
                if (!ev.isRecurring || !ev.recurringInterval) return;
                const parsed = this._parseEventDate(ev.date);
                if (!parsed) return;

                let endDate = cap;
                if (ev.recurringEndDate) {
                    const d = new Date(ev.recurringEndDate + 'T23:59:59');
                    if (!isNaN(d)) endDate = d < cap ? d : cap;
                }

                const excluded = excludedSet(ev);
                let current = parsed.date;
                let idx = 1;
                while (true) {
                    current = this._addInterval(current, ev.recurringInterval);
                    if (!current || current > endDate) break;
                    const dateKey = this._toDateKey(current);
                    if (!excluded.has(dateKey)) {
                        const isoDate = ev.date.includes('T')
                            ? new Date(current).toISOString()
                            : dateKey;
                        extra.push({ ...ev, variantId: `${ev.variantId}-${idx}`, date: isoDate });
                    }
                    idx++;
                }
            });
            return [...events, ...extra];
        }

        // â”€â”€ Filters & Legend â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

        _getUniqueCategories() {
            const map = new Map(); // category → color (product takes precedence over blog)
            // First pass: product variant events — establishes categories with their colors
            this.events.forEach((ev) => {
                if (ev.isBlogEvent) return;
                const cats = Array.isArray(ev.categories) ? ev.categories : [];
                cats.forEach((cat) => {
                    if (!map.has(cat)) map.set(cat, ev.color || null);
                });
            });
            // Second pass: blog events — only add categories not already covered by a product
            this.events.forEach((ev) => {
                if (!ev.isBlogEvent) return;
                const cats = Array.isArray(ev.categories) ? ev.categories : [];
                cats.forEach((cat) => {
                    if (!map.has(cat)) map.set(cat, ev.color || null);
                });
            });
            return map;
        }

        _buildFilters() {
            if (!this.filtersEl) return;
            const cats = this._getUniqueCategories();
            if (cats.size === 0) {
                this.filtersEl.style.display = 'none';
                return;
            }
            const container = document.createElement('div');
            container.className = 'cal__filter-pills';
            container.setAttribute('role', 'group');
            container.setAttribute('aria-label', 'Filter by category');

            const allPill = document.createElement('button');
            allPill.type = 'button';
            allPill.className = 'cal__filter-pill cal__filter-pill--active';
            allPill.dataset.filterValue = '__all__';
            allPill.textContent = this.text.allCategories;
            allPill.addEventListener('click', () => {
                this.activeFilters.clear();
                this._syncFilterUi();
                this._render();
            });
            container.appendChild(allPill);

            cats.forEach((color, cat) => {
                const pill = document.createElement('button');
                pill.type = 'button';
                pill.className = 'cal__filter-pill';
                pill.dataset.filterValue = cat;
                pill.textContent = cat;
                if (color) pill.style.setProperty('--pill-color', color);
                pill.addEventListener('click', () => {
                    if (this.activeFilters.has(cat)) {
                        this.activeFilters.delete(cat);
                    } else {
                        this.activeFilters.add(cat);
                    }
                    this._syncFilterUi();
                    this._render();
                });
                container.appendChild(pill);
            });

            this.filtersEl.innerHTML = '';
            this.filtersEl.appendChild(container);
        }

        _syncFilterUi() {
            const pills = this.filtersEl && this.filtersEl.querySelectorAll('.cal__filter-pill');
            if (!pills) return;
            pills.forEach((pill) => {
                const val = pill.dataset.filterValue;
                const isActive = val === '__all__'
                    ? this.activeFilters.size === 0
                    : this.activeFilters.has(val);
                pill.classList.toggle('cal__filter-pill--active', isActive);
            });
        }

        _buildLegend() {
            if (!this.legendEl) return;
            const cats = this._getUniqueCategories();
            if (cats.size === 0) {
                this.legendEl.style.display = 'none';
                return;
            }
            let html = '';
            cats.forEach((color, cat) => {
                const dotStyle = color ? `background:${this._esc(color)}` : 'background:currentColor;opacity:0.4';
                html += `<span class="cal__legend-item"><span class="cal__legend-dot" style="${dotStyle}"></span>${this._esc(cat)}</span>`;
            });
            this.legendEl.innerHTML = html;
        }

        // â”€â”€ Navigation â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

        _navigate(dir) {
            if (this.mobileView === 'cards' || this.view === 'month') {
                this.cursor = new Date(this.cursor.getFullYear(), this.cursor.getMonth() + dir, 1);
            } else {
                this.cursor = new Date(this.cursor.getTime() + dir * 7 * 86400000);
            }
            this._render();
        }

        _goToToday() {
            this.cursor = new Date(this.today);
            this._render();
        }

        _setView(view) {
            this.view = view;
            this.querySelectorAll('.cal__view-btn').forEach((btn) => {
                btn.classList.toggle('cal__view-btn--active', btn.dataset.view === view);
            });
            this.calEl.dataset.view = view;
            this._buildWeekdays();
            this._render();
        }

        // â”€â”€ Weekday labels â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

        _buildWeekdays() {
            if (this.view === 'week') {
                this.weekdaysEl.innerHTML = '';
                return;
            }
            const lang = document.documentElement.lang || 'sv';
            const allDays = Array.from({ length: 7 }, (_, i) => {
                const d = new Date(2023, 0, 1 + ((i + this.firstDay) % 7));
                return d.toLocaleDateString(lang, { weekday: 'short' });
            });
            this.weekdaysEl.innerHTML = allDays
                .map((d) => `<span class="cal__weekday">${d}</span>`)
                .join('');
        }

        // â”€â”€ Render â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

        _render() {
            this._closeDayPanel();
            const isMobileCards = window.matchMedia('(max-width: 900px)').matches && this.mobileView === 'cards';
            this.dataset.mobileView = this.mobileView;
            if (isMobileCards) {
                this._renderCardView();
            } else {
                if (this.view === 'week') {
                    this._renderWeek();
                } else {
                    this._renderMonth();
                }
                this._bindMobileDayTap();
            }
        }

        _renderEventHtml(ev) {
            const p = ev._parsed || this._parseEventDate(ev.date);
            if (!p) return '';
            const label = (ev.variantTitle && ev.variantTitle !== 'Default Title')
                ? ev.variantTitle
                : ev.title;
            const color = ev.color || '';
            const colorStyle = color ? `--cal-color:${color}` : '';
            const soldout = ev.expired ? ' cal__event--expired' : !ev.available ? ' cal__event--soldout' : '';
            const timeHtml = p.hasTime ? `<span class="cal__event-time">${this._esc(p.timeStr)}</span>` : '';
            const locations = Array.isArray(ev.locations) ? ev.locations : [];
            const locationTitle = locations.length ? ` title="${this._esc(locations.join(', '))}"` : '';
            return `<div class="cal__event${soldout}" data-variant-id="${ev.variantId}" style="${colorStyle}" role="button" tabindex="0" aria-label="${this._esc(label)}"${locationTitle}>
  <span class="cal__event-dot" aria-hidden="true"></span>
  ${timeHtml}
  <span class="cal__event-label">${this._esc(label)}</span>
</div>`;
        }

        _renderDayHtml(date, { isOutside = false, showDayName = false } = {}) {
            const dateKey = this._toDateKey(date);
            const isToday = dateKey === this._toDateKey(this.today);
            const isWeekend = date.getDay() === 0 || date.getDay() === 6;
            const dayEvents = this._getEventsForDate(dateKey);
            const lang = document.documentElement.lang || 'sv';
            const dayName = showDayName ? date.toLocaleDateString(lang, { weekday: 'long' }) : '';

            const classes = [
                'cal__day',
                isOutside ? 'cal__day--outside' : '',
                isToday ? 'cal__day--today' : '',
                isWeekend ? 'cal__day--weekend' : '',
                dayEvents.length ? 'cal__day--has-events' : '',
            ].filter(Boolean).join(' ');

            return `<div class="${classes}" data-date="${dateKey}" role="gridcell" aria-label="${date.getDate()}">
  <div class="cal__day-header">
    ${showDayName ? `<span class="cal__day-name">${this._esc(dayName)}</span>` : ''}
    <span class="cal__day-number">${date.getDate()}</span>
  </div>
  ${dayEvents.length ? '<span class="cal__day-dot" aria-hidden="true"></span>' : ''}
  <div class="cal__events">${dayEvents.map((ev) => this._renderEventHtml(ev)).join('')}</div>
</div>`;
        }

        _renderMonth() {
            const year = this.cursor.getFullYear();
            const month = this.cursor.getMonth();
            const lang = document.documentElement.lang || 'sv';

            this.titleEl.textContent = new Intl.DateTimeFormat(lang, {
                month: 'long',
                year: 'numeric',
            }).format(new Date(year, month, 1));

            this.calEl.dataset.view = 'month';
            this.gridEl.className = 'cal__grid cal__grid--month';

            const firstOfMonth = new Date(year, month, 1);
            const offset = (firstOfMonth.getDay() - this.firstDay + 7) % 7;
            const daysInMonth = new Date(year, month + 1, 0).getDate();
            const totalCells = Math.ceil((offset + daysInMonth) / 7) * 7;

            let html = '';
            for (let i = 0; i < totalCells; i++) {
                const date = new Date(year, month, 1 - offset + i);
                const isOutside = i < offset || i >= offset + daysInMonth;
                html += this._renderDayHtml(date, { isOutside });
            }
            this.gridEl.innerHTML = html;
        }

        _renderWeek() {
            const lang = document.documentElement.lang || 'sv';
            const dayOffset = (this.cursor.getDay() - this.firstDay + 7) % 7;
            const weekStart = new Date(this.cursor);
            weekStart.setDate(weekStart.getDate() - dayOffset);
            weekStart.setHours(0, 0, 0, 0);

            const weekEnd = new Date(weekStart);
            weekEnd.setDate(weekEnd.getDate() + 6);

            const fmtOpts = { day: 'numeric', month: 'short' };
            const startStr = weekStart.toLocaleDateString(lang, fmtOpts);
            const endStr = weekEnd.toLocaleDateString(lang, { ...fmtOpts, year: 'numeric' });
            this.titleEl.textContent = `${startStr} \u2013 ${endStr}`;

            this.calEl.dataset.view = 'week';
            this.gridEl.className = 'cal__grid cal__grid--week';

            let html = '';
            for (let i = 0; i < 7; i++) {
                const date = new Date(weekStart);
                date.setDate(date.getDate() + i);
                html += this._renderDayHtml(date, { showDayName: true });
            }
            this.gridEl.innerHTML = html;
        }

        // -- Mobile card view --

        _setMobileView(view) {
            this.mobileView = view;
            this.querySelectorAll('.cal__mobile-view-btn').forEach((btn) => {
                btn.classList.toggle('cal__mobile-view-btn--active', btn.dataset.mobileView === view);
            });
            this._render();
        }

        _renderCardView() {
            if (!this.cardListEl) return;
            const year = this.cursor.getFullYear();
            const month = this.cursor.getMonth();
            const lang = document.documentElement.lang || 'sv';

            if (this.titleEl) {
                this.titleEl.textContent = new Intl.DateTimeFormat(lang, {
                    month: 'long',
                    year: 'numeric',
                }).format(new Date(year, month, 1));
            }

            const daysInMonth = new Date(year, month + 1, 0).getDate();
            const groups = [];
            for (let d = 1; d <= daysInMonth; d++) {
                const date = new Date(year, month, d);
                const dateKey = this._toDateKey(date);
                const dayEvents = this._getEventsForDate(dateKey);
                if (dayEvents.length) groups.push({ date, dateKey, events: dayEvents });
            }

            if (groups.length === 0) {
                this.cardListEl.innerHTML = `<p class="cal__cards-empty">${this._esc(this.text.noEvents)}</p>`;
                return;
            }

            let html = '';
            groups.forEach(({ date, events }) => {
                const isToday = this._toDateKey(date) === this._toDateKey(this.today);
                const dateLabel = date.toLocaleDateString(lang, { weekday: 'long', day: 'numeric', month: 'long' });
                html += `<div class="cal__card-group">
  <h3 class="cal__card-date-header${isToday ? ' cal__card-date-header--today' : ''}">${this._esc(dateLabel)}</h3>
  <div class="cal__card-items">
    ${events.map((ev) => this._renderEventCardHtml(ev)).join('')}
  </div>
</div>`;
            });
            this.cardListEl.innerHTML = html;
        }

        _renderEventCardHtml(ev) {
            const p = ev._parsed || this._parseEventDate(ev.date);
            const label = (ev.variantTitle && ev.variantTitle !== 'Default Title') ? ev.variantTitle : ev.title;
            const color = ev.color || '';
            const soldout = ev.isBlogEvent ? false : (ev.expired || !ev.available);
            const cats = Array.isArray(ev.categories) ? ev.categories : [];

            const timeHtml = (p && p.hasTime)
                ? `<span class="cal__event-card-time">${this._esc(p.timeStr)}</span>`
                : '';
            const imgHtml = ev.image
                ? `<img class="cal__event-card-img" src="${this._esc(ev.image)}" alt="" loading="lazy">`
                : '';
            const badgesHtml = cats.map((cat) => {
                const bg = color ? ` style="background:${this._esc(color)}"` : '';
                return `<span class="cal__badge"${bg}>${this._esc(cat)}</span>`;
            }).join('');
            const soldoutBadge = ev.isBlogEvent ? '' : (ev.expired ? `<span class="cal__badge cal__badge--expired">${this._esc(this.text.expired)}</span>` : !ev.available ? `<span class="cal__badge cal__badge--soldout">${this._esc(this.text.soldout)}</span>` : '');
            const colorBorder = color ? ` style="border-left-color:${color}"` : '';
            const variantHtml = (ev.variantTitle && ev.variantTitle !== 'Default Title')
                ? `<span class="cal__event-card-variant">${this._esc(ev.variantTitle)}</span>`
                : '';
            const cardLocations = Array.isArray(ev.locations) ? ev.locations : [];
            const cardLocationHtml = cardLocations.length
                ? `<span class="cal__event-card-location">
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>
  ${this._esc(cardLocations.join(', '))}
</span>`
                : '';
            const shareCardHtml = `<button class="cal__event-card-share cal__share-btn" type="button" data-variant-id="${ev.variantId}" aria-label="Dela" title="Dela">
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><circle cx="18" cy="5" r="3"></circle><circle cx="6" cy="12" r="3"></circle><circle cx="18" cy="19" r="3"></circle><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line></svg>
</button>`;

            const inventoryHtml = this._inventoryHtml(ev);
            return `<div class="cal__event-card${ev.expired ? ' cal__event-card--expired' : soldout ? ' cal__event-card--soldout' : ''}" data-variant-id="${ev.variantId}"${colorBorder} role="button" tabindex="0" aria-label="${this._esc(label)}">
  ${imgHtml}
  <div class="cal__event-card-body">
    ${(badgesHtml || soldoutBadge) ? `<div class="cal__event-card-badges">${badgesHtml}${soldoutBadge}</div>` : ''}
    <span class="cal__event-card-title">${this._esc(ev.title)}</span>
    ${variantHtml}
    <div class="cal__event-card-footer">
      ${timeHtml}
      ${ev.isBlogEvent ? '' : `<span class="cal__event-card-price">${this._esc(ev.priceFormatted)}</span>`}
      ${shareCardHtml}
    </div>
    ${inventoryHtml}
    ${cardLocationHtml}
  </div>
</div>`;
        }

        // -- Detail panel --

        // ── Blog event detail ──────────────────────────────────────────────

        _renderBlogEventDetailHtml(ev) {
            const p = this._parseEventDate(ev.date);
            const lang = document.documentElement.lang || 'sv';
            const color = ev.color || null;

            let dateStr = '';
            if (p) {
                if (p.hasTime) {
                    dateStr = p.date.toLocaleDateString(lang, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
                    dateStr += ` ${this.text.timePrefix}${p.timeStr}`;
                } else {
                    dateStr = p.date.toLocaleDateString(lang, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
                }
            }

            const cats = Array.isArray(ev.categories) ? ev.categories : [];
            const badgesHtml = cats.map((cat) => {
                const bg = color ? `style="background:${this._esc(color)}"` : '';
                return `<span class="cal__badge" ${bg}>${this._esc(cat)}</span>`;
            }).join('');

            const imgHtml = ev.largeImage
                ? `<img class="cal__detail-image" src="${this._esc(ev.largeImage)}" alt="${this._esc(ev.title)}" loading="lazy">`
                : '';

            const dateHtml = dateStr
                ? `<div class="cal__detail-date">
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
  <span>${this._esc(dateStr)}</span>
</div>`
                : '';

            const descHtml = ev.description
                ? `<div class="cal__detail-desc">${ev.description}</div>`
                : '';

            const readMoreHtml = `<a class="cal__detail-link" href="${this._esc(ev.url)}">${this._esc(this.text.readMore)}</a>`;

            const shareHtml = `<button class="cal__detail-share cal__share-btn" type="button" data-variant-id="${ev.variantId}" aria-label="Dela event">
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><circle cx="18" cy="5" r="3"></circle><circle cx="6" cy="12" r="3"></circle><circle cx="18" cy="19" r="3"></circle><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line></svg>
  ${this._esc(this.text.share)}
</button>`;

            return `<div class="cal__detail-inner" data-variant-id="${ev.variantId}">
  ${imgHtml}
  <div class="cal__detail-body">
    ${badgesHtml ? `<div class="cal__detail-badges">${badgesHtml}</div>` : ''}
    <h3 class="cal__detail-title">${this._esc(ev.title)}</h3>
    ${dateHtml}
    ${descHtml}
    <div class="cal__detail-footer-links">
      ${readMoreHtml}
      ${shareHtml}
    </div>
  </div>
</div>`;
        }

        _renderDetailHtml(ev) {
            if (ev.isBlogEvent) return this._renderBlogEventDetailHtml(ev);
            const lang = document.documentElement.lang || 'sv';
            const label = (ev.variantTitle && ev.variantTitle !== 'Default Title') ? ev.variantTitle : null;
            const color = ev.color || null;
            const p = this._parseEventDate(ev.date);

            // Formatted date + time
            let dateStr = '';
            if (p) {
                if (p.hasTime) {
                    dateStr = p.date.toLocaleDateString(lang, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
                    dateStr += ` ${this.text.timePrefix}${p.timeStr}`;
                } else {
                    dateStr = p.date.toLocaleDateString(lang, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
                }
            }

            // Category badges
            const cats = Array.isArray(ev.categories) ? ev.categories : [];
            const badgesHtml = cats.map((cat) => {
                const bg = color ? `style="background:${this._esc(color)}"` : '';
                return `<span class="cal__badge" ${bg}>${this._esc(cat)}</span>`;
            }).join('');

            // Soldout / expired badge
            const soldoutBadge = ev.expired
                ? `<span class="cal__badge cal__badge--expired">${this._esc(this.text.expired)}</span>`
                : !ev.available
                    ? `<span class="cal__badge cal__badge--soldout">${this._esc(this.text.soldout)}</span>`
                    : '';

            // Image
            const imgHtml = ev.largeImage
                ? `<img class="cal__detail-image" src="${this._esc(ev.largeImage)}" alt="${this._esc(ev.title)}" loading="lazy">`
                : '';

            // Variant title line
            const variantHtml = label
                ? `<p class="cal__detail-variant">${this._esc(label)}</p>`
                : '';

            // Date line
            const dateHtml = dateStr
                ? `<div class="cal__detail-date">
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
  <span>${this._esc(dateStr)}</span>
</div>`
                : '';

            // Description (Shopify product.description â€” merchant-controlled HTML)
            const descHtml = ev.description
                ? `<div class="cal__detail-desc">${ev.description}</div>`
                : '';

            // Location
            const locations = Array.isArray(ev.locations) ? ev.locations : [];
            const locationHtml = locations.length
                ? `<div class="cal__detail-location">
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>
  <span>${this._esc(locations.join(', '))}</span>
</div>`
                : '';

            // Price + ATC
            const atcHtml = ev.expired
                ? `<button class="cal__detail-atc cal__detail-atc--expired" type="button" disabled aria-disabled="true">${this._esc(this.text.expired)}</button>`
                : ev.available
                    ? `<button class="cal__detail-atc" type="button" data-variant-id="${ev.variantId}">${this._esc(this.text.buyTickets)}</button>`
                    : `<button class="cal__detail-atc cal__detail-atc--soldout" type="button" disabled aria-disabled="true">${this._esc(this.text.soldout)}</button>`;

            const priceHtml = `<span class="cal__detail-price">${this._esc(ev.priceFormatted)}</span>`;

            // Product page link + share
            const linkHtml = `<a class="cal__detail-link" href="${this._esc(ev.url)}">${this._esc(this.text.allDates)}</a>`;
            const shareHtml = `<button class="cal__detail-share cal__share-btn" type="button" data-variant-id="${ev.variantId}" aria-label="Dela event">
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><circle cx="18" cy="5" r="3"></circle><circle cx="6" cy="12" r="3"></circle><circle cx="18" cy="19" r="3"></circle><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line></svg>
  ${this._esc(this.text.share)}
</button>`;

            const inventoryHtml = this._inventoryHtml(ev);
            return `<div class="cal__detail-inner" data-variant-id="${ev.variantId}">
  ${imgHtml}
  <div class="cal__detail-body">
    ${badgesHtml || soldoutBadge ? `<div class="cal__detail-badges">${badgesHtml}${soldoutBadge}</div>` : ''}
    <h3 class="cal__detail-title">${this._esc(ev.title)}</h3>
    ${variantHtml}
    ${dateHtml}
    ${inventoryHtml}
    ${locationHtml}
    ${descHtml}
    <div class="cal__detail-actions">
      ${priceHtml}
      ${atcHtml}
    </div>
    <div class="cal__detail-footer-links">
      ${linkHtml}
      ${shareHtml}
    </div>
  </div>
</div>`;
        }

        _showDetail(ev) {
            const html = this._renderDetailHtml(ev);
            const isMobile = window.matchMedia('(max-width: 900px)').matches;

            if (isMobile) {
                if (this.drawerContentEl) {
                    this.drawerContentEl.innerHTML = html;
                    this._bindDetailAtc(this.drawerContentEl);
                }
                this._openDrawer();
            } else {
                if (this.detailEl) {
                    this.detailEl.innerHTML = html;
                    this._bindDetailAtc(this.detailEl);
                    this.detailEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                }
            }
        }

        _bindDetailAtc(container) {
            container.querySelectorAll('.cal__detail-atc:not([disabled])').forEach((btn) => {
                btn.addEventListener('click', () => {
                    this._addToCart(btn.dataset.variantId, btn);
                });
            });
        }

        // â”€â”€ Mobile drawer â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

        _openDrawer() {
            if (!this.drawerEl) return;
            this.drawerEl.classList.add('cal__drawer--open');
            this.drawerEl.setAttribute('aria-hidden', 'false');
            document.body.style.overflow = 'hidden';
        }

        _closeDrawer() {
            if (!this.drawerEl) return;
            this.drawerEl.classList.remove('cal__drawer--open');
            this.drawerEl.setAttribute('aria-hidden', 'true');
            document.body.style.overflow = '';
        }

        // â”€â”€ Mobile day tap (month view) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

        _isMobile() {
            return window.matchMedia('(max-width: 480px)').matches;
        }

        _closeDayPanel() {
            const panel = this.querySelector('.cal__day-panel');
            if (panel) panel.remove();
            this.gridEl.querySelectorAll('.cal__day--selected').forEach((d) =>
                d.classList.remove('cal__day--selected')
            );
        }

        _bindMobileDayTap() {
            if (!this._isMobile() || this.view !== 'month') return;
            this.gridEl.querySelectorAll('.cal__day--has-events').forEach((cell) => {
                cell.addEventListener('click', (e) => {
                    if (e.target.closest('.cal__event')) return; // handled by delegation
                    const dateKey = cell.dataset.date;
                    if (!dateKey) return;
                    const alreadySelected = cell.classList.contains('cal__day--selected');
                    this._closeDayPanel();
                    if (alreadySelected) return;

                    cell.classList.add('cal__day--selected');
                    const events = this._getEventsForDate(dateKey);
                    if (!events.length) return;

                    const [y, m, d] = dateKey.split('-').map(Number);
                    const date = new Date(y, m - 1, d);
                    const lang = document.documentElement.lang || 'sv';
                    const heading = date.toLocaleDateString(lang, {
                        weekday: 'long',
                        month: 'long',
                        day: 'numeric',
                    });

                    const panel = document.createElement('div');
                    panel.className = 'cal__day-panel';
                    panel.dataset.date = dateKey;
                    panel.innerHTML = `<div class="cal__day-panel-header">
  <span class="cal__day-panel-title">${this._esc(heading)}</span>
  <button class="cal__day-panel-close" type="button" aria-label="Stäng">&#x2715;</button>
</div>
<div class="cal__day-panel-events">
  ${events.map((ev) => this._renderEventHtml(ev)).join('')}
</div>`;

                    this.gridEl.after(panel);
                    panel.querySelector('.cal__day-panel-close').addEventListener('click', () =>
                        this._closeDayPanel()
                    );
                    panel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                });
            });
        }

        // â”€â”€ Add to Cart â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

        _inventoryHtml(ev) {
            if (ev.isBlogEvent || ev.expired || !ev.available || !this.text.inventory) return '';
            if (ev.inventoryQuantity == null || ev.inventoryQuantity <= 0) return '';
            const text = this.text.inventory.replace('[inventory]', ev.inventoryQuantity);
            return `<div class="cal__inventory">
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
  <span>${this._esc(text)}</span>
</div>`;
        }

        _getVariantUrl(ev) {
            const baseId = ev._baseVariantId || ev.variantId;
            return window.location.origin + ev.url + '?variant=' + baseId;
        }

        async _shareEvent(ev) {
            const url = this._getVariantUrl(ev);
            if (navigator.share) {
                try {
                    await navigator.share({ title: ev.title, url });
                } catch (err) {
                    if (err.name !== 'AbortError') this._copyToClipboard(url);
                }
            } else {
                this._copyToClipboard(url);
            }
        }

        _copyToClipboard(text) {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(text).then(
                    () => this._showToast('Länk kopierad!'),
                    () => this._showToast('Kunde inte kopiera länken.', true)
                );
            } else {
                try {
                    const ta = document.createElement('textarea');
                    ta.value = text;
                    ta.style.cssText = 'position:fixed;opacity:0';
                    document.body.appendChild(ta);
                    ta.select();
                    document.execCommand('copy');
                    document.body.removeChild(ta);
                    this._showToast('Länk kopierad!');
                } catch {
                    this._showToast('Kunde inte kopiera länken.', true);
                }
            }
        }

        async _addToCart(variantId, btn) {
            const originalText = btn.textContent;
            btn.disabled = true;
            btn.textContent = '...';

            const cartUrl =
                (window.FoxTheme && window.FoxTheme.routes && window.FoxTheme.routes.cart_add_url) ||
                '/cart/add.js';

            try {
                const response = await fetch(cartUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                    body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 }),
                });

                const data = await response.json();

                if (!response.ok || data.status === 422 || data.errors) {
                    const errMsg =
                        (data.errors && (typeof data.errors === 'string' ? data.errors : Object.values(data.errors)[0])) ||
                        'Could not add to cart.';
                    this._showToast(errMsg, true);
                    btn.disabled = false;
                    btn.textContent = originalText;
                    return;
                }

                document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
                this._showToast('Tillagd i varukorgen!');
                setTimeout(() => {
                    btn.disabled = false;
                    btn.textContent = originalText;
                }, 2000);
            } catch {
                this._showToast('Något gick fel.', true);
                btn.disabled = false;
                btn.textContent = originalText;
            }
        }

        _showToast(msg, isError = false) {
            if (!this.toastEl) return;
            this.toastEl.textContent = msg;
            this.toastEl.classList.toggle('cal__toast--error', isError);
            this.toastEl.classList.add('cal__toast--visible');
            clearTimeout(this._toastTimer);
            this._toastTimer = setTimeout(() => {
                this.toastEl.classList.remove('cal__toast--visible');
            }, 2500);
        }
    }

    customElements.define('calendar-section', CalendarSection);
}

